import React, { useEffect, useState } from "react";
import { Accordion, Alert, Badge, Button, Card, Form, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { useRouteLoaderData } from "react-router-dom";

import { useAppApi } from "@/App";
import { GameFeaturingLaunchState } from "@/const";
import { DevForm } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { DrawProgressBar } from "@components/Game/Misc";
import { Awesome, DevModal } from "@components/Helpers";
import { CommonCard, ListingCard } from "@components/Pages";
import { parseAndLinkify, url_reverse } from "@components/Utils";

const ConditionalWrapper = ({ condition, wrapper, children }) => (condition ? wrapper(children) : children);

const StateBar = ({ state }) => {
    const { t } = useTranslation();
    const labels = [
        t("Черновик"),
        t("Маркетинговое ревью"),
        state === GameFeaturingLaunchState.REJECTED ? t("Материалы отклонены") : t("Ревью пройдено"),
        t("Промо запущено"),
    ];

    let active = 0;
    switch (state) {
        case GameFeaturingLaunchState.DRAFT:
            active = 1;
            break;
        case GameFeaturingLaunchState.MARKETING_REVIEW:
            active = 2;
            break;
        case GameFeaturingLaunchState.REJECTED:
        case GameFeaturingLaunchState.REVIEW_PASSED:
            active = 3;
            break;
        case GameFeaturingLaunchState.PROMO_LAUNCHED:
            active = 100;
            break;
    }

    return (
        <div className="mb-3">
            <DrawProgressBar labels={labels} active={active} now={25} />
        </div>
    );
};

const SubmitModal = ({ show, setShow, blocks, setApiData, onFormSubmit }) => {
    const { t } = useTranslation();
    const handleClose = () => setShow(false);
    const [warning, setWarning] = useState(false);

    useEffect(() => {
        setWarning(false);
        blocks.forEach((block) => {
            if (block.fieldsFillStatus === "warning") {
                setWarning(true);
            }
        });
    }, [blocks]);

    return (
        <DevModal
            show={show}
            onHide={handleClose}
            header={t("Отправка на проверку")}
            body={
                <>
                    {warning && <p>{t("Загружены не все рекламные форматы в следующих блоках:")}</p>}
                    {warning && (
                        <ul>
                            {blocks.map(
                                (block, index) =>
                                    block.fieldsFillStatus === "warning" && <li key={`b-${index}`}>{block.name}</li>
                            )}
                        </ul>
                    )}
                    {warning && (
                        <p>
                            {t(
                                "К сожалению, дослать недостающие материалы позднее будет нельзя. " +
                                    "Нажимая «Отправить» вы соглашаетесь с тем, что базовое " +
                                    "промо может быть откручено не в полном объеме."
                            )}
                        </p>
                    )}
                    {!warning && <p>{t("Все данные заполнены. Подтвердите отправку на проверку")}</p>}
                </>
            }
            footer={
                <>
                    <DevForm.Button
                        variant="primary"
                        name="action"
                        value="moderate"
                        onClick={(ev) =>
                            onFormSubmit(ev, {
                                onOK: (resp) => {
                                    setApiData(resp);
                                    setShow(false);
                                },
                            })
                        }
                    >
                        {t("Отправить")}
                    </DevForm.Button>
                    <Button variant="secondary" onClick={handleClose}>
                        {t("Отмена")}
                    </Button>
                </>
            }
        />
    );
};

const AccordionBlockState = ({ status, index }) => {
    const { t } = useTranslation();
    const [message, setMessage] = useState("Тест");

    useEffect(() => {
        if (status === "warning") {
            setMessage(t("Не все данные заполнены"));
        }

        if (status === "success") {
            setMessage(t("Все данные заполнены"));
        }
    }, [status]);

    return (
        <>
            {status && (
                <span
                    key={`tip-${index}`}
                    className={status === "warning" ? "text-warning" : "text-success"}
                    style={{ opacity: status === "success" ? 0 : 1 }} // Скрываем зеленые галочки
                >
                    {status === "success" && <Awesome type="check" tooltip={message} />}
                    {status === "warning" && <Awesome type="exclamation-triangle" tooltip={message} />}
                </span>
            )}
        </>
    );
};

const AccordionBlockModerationBadge = ({ isBlockRejected, promoState, block }) => {
    const { t } = useTranslation();
    const { fieldsFillStatus } = block;

    if (fieldsFillStatus === "warning") {
        return <Badge variant={"secondary"}>{t("Этот формат не будет размещен")}</Badge>;
    }

    if (
        (promoState === GameFeaturingLaunchState.REJECTED || promoState === GameFeaturingLaunchState.DRAFT) &&
        isBlockRejected
    ) {
        return (
            <Badge variant={isBlockRejected ? "danger" : "success"}>
                {isBlockRejected && <>{isBlockRejected ? t("Отклонено") : t("Одобрено")}</>}
            </Badge>
        );
    }
};

const PreviewButton = ({ apiData, block }) => {
    const commonInfo = useRouteLoaderData("commonInfo");
    const { t } = useTranslation();
    const [isChanged, setIsChanged] = useState(false);

    useEffect(() => {
        if (apiData.object) {
            const changed = block.fields.some((field) => apiData.forms_data.main.data[field] !== apiData.object[field]);
            setIsChanged(changed);
        }
    }, [apiData.forms_data.main.data, apiData.object, block.fields]);

    if (apiData.object?.game_featuring_identifier && !isChanged) {
        return (
            <DevForm.Button
                href={`//${commonInfo.domain_showcase}/promo_preview/${apiData.object.game_featuring_identifier}/?block=${block.id}`}
                variant="primary"
                size="sm"
                className="ml-2"
                target="_blank"
                tooltip={t("Предпросмотр")}
            >
                <Awesome type="eye" />
            </DevForm.Button>
        );
    }
    return <></>;
};

const AccordionBlock = ({ block, fieldProps, index, apiData }) => {
    const comment = fieldProps.apiData.forms_data.comments?.data[block.comment_field];
    const isPromoRejected = apiData.object?.state === GameFeaturingLaunchState.REJECTED;
    const isBlockRejected = comment && comment !== "";

    // Блочим поля для блока, который прошел модерацию
    if (!isBlockRejected && isPromoRejected && block.fieldsFillStatus === "success") {
        block.fields.forEach((field) => {
            if (apiData.object[field] !== null && apiData.object[field] !== "") {
                fieldProps.apiData.forms_data.main.fields[field].disabled = true;
            }
        });
    }

    return (
        <Card style={{ cursor: "pointer" }}>
            <Accordion.Toggle as={Card.Header} eventKey={`event-${index}`}>
                <div className={"d-flex align-items-center"}>
                    <AccordionBlockState status={block.fieldsFillStatus} index={index} />
                    <strong className={"ml-3"}>{block.name}</strong>
                    <div className={"ml-auto"}>
                        <AccordionBlockModerationBadge
                            promoState={apiData.object?.state}
                            isBlockRejected={isBlockRejected}
                            block={block}
                        />
                        {block.fieldsFillStatus === "success" && block.id !== "block_vk1" && (
                            <PreviewButton apiData={apiData} block={block} />
                        )}
                    </div>
                </div>
            </Accordion.Toggle>
            <Accordion.Collapse eventKey={`event-${index}`}>
                <Card.Body>
                    {block.fields.map((field) => (
                        <DevForm.Field key={`field-${field}`} {...fieldProps} name={field} />
                    ))}
                    {isBlockRejected && (
                        <DevForm.Field
                            key={`field-comment-${block.comment_field}`}
                            {...fieldProps}
                            prefix="comments"
                            name={block.comment_field}
                        />
                    )}
                </Card.Body>
            </Accordion.Collapse>
        </Card>
    );
};

const DeveloperModerMessages = ({ f_messages }) => {
    const { t } = useTranslation();
    return (
        <>
            <ListingCard header={t("Сообщения")}>
                <Table hover responsive className="dmg-table">
                    <thead>
                        <tr>
                            <th>{t("Дата")}</th>
                            <th>{t("Тип")}</th>
                            <th>{t("Текст")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {f_messages?.map((o) => (
                            <tr key={`message${o.id}`}>
                                <td>{o.date_added}</td>
                                <td>
                                    <Badge className="ml-auto" variant={o.sender === "developer" ? "success" : "info"}>
                                        {o.sender === "developer" ? t("от разработчика") : t("от модератора")}
                                    </Badge>
                                </td>
                                <td className="show-wrap-text">{parseAndLinkify(o.message)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </ListingCard>
        </>
    );
};

export default function FeaturingLaunch({ gameId, header }) {
    const { t } = useTranslation();
    const commonInfo = useRouteLoaderData("commonInfo");
    const { apiData, setApiData } = useAppApi(url_reverse("api:game:featuring_launch", { game_id: gameId }));
    const onFormSubmit = useSubmit();
    const [show, setShow] = useState(false);
    const [disableSendButton, setDisableSendButton] = useState(false);
    const [blocks, setBlocks] = useState([
        {
            id: "block_slider_vkplay",
            name: t("Раздел «слайдер на VK Play»"),
            fields: ["slider_description", "slider_art", "slider_logo_vkplay"],
            fieldsFillStatus: undefined,
            comment_field: "block_slider_vkplay_moderator_comment",
        },
        {
            id: "block_popup",
            name: t("Поп-ап в лаунчере VK Play"),
            fields: ["popup_picture", "popup_logo", "popup_text"],
            fieldsFillStatus: undefined,
            comment_field: "block_popup_moderator_comment",
        },
        {
            id: "block_vk1",
            name: t("Баннер в рамках экосистемы VK #1"),
            fields: ["banner1_picture"],
            fieldsFillStatus: undefined,
            comment_field: "block_vk1_moderator_comment",
        },
        {
            id: "block_vk2",
            name: t("Баннер в рамках экосистемы VK #2"),
            fields: ["banner2_title", "banner2_descr", "banner2_picture", "banner2_picture_2"],
            fieldsFillStatus: undefined,
            comment_field: "block_vk2_moderator_comment",
        },
    ]);
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");

    useEffect(() => {
        if (apiData?.forms_data) {
            const newBlocks = [...blocks];
            newBlocks.map((block) => {
                const hasEmptyField = block.fields.find((field) => {
                    const fieldData = apiData.forms_data.main.data[field];
                    return fieldData == null || fieldData === "null" || fieldData === "";
                });

                block.fieldsFillStatus = hasEmptyField ? "warning" : "success";
                return block;
            });
            setBlocks(newBlocks);
        }

        setDisableSendButton(!apiData?.force_allow_basic_promotion_button && !apiData?.is_game_released);
    }, [apiData]);

    if (!apiData) return;

    const state = apiData.object?.state;

    const fieldProps = {
        apiData,
        setApiData,
        prefix: "main",
    };

    if (apiData.status === "error") {
        return (
            <CommonCard header={header}>
                <Alert variant="danger">
                    <div dangerouslySetInnerHTML={{ __html: apiData.error_message }} />
                </Alert>
            </CommonCard>
        );
    }

    return (
        <CommonCard header={header}>
            <StateBar state={state} />
            <Alert variant="warning">
                {t(
                    "Обратите внимание, отправить заявку на базовое промо можно в течение 6 месяцев с момента запуска игры на vkplay.ru."
                )}
            </Alert>
            {state === GameFeaturingLaunchState.MODERATE && (
                <div className="dmg-callout dmg-callout-info mb-3">
                    {t("Проверка может занимать до 3 рабочих дней.")}
                </div>
            )}
            {state === GameFeaturingLaunchState.REJECTED && (
                <div className="dmg-callout dmg-callout-info mb-3">
                    {t(
                        "Модератор нашел ошибку в материалах. Исправьте ошибку и отправьте материалы еще раз на модерацию."
                    )}
                </div>
            )}
            {(!state || state === GameFeaturingLaunchState.DRAFT) && (
                <Alert variant="info">
                    <Trans>
                        Для получения базового промо необходимо загрузить рекламные материалы по{" "}
                        <a href="https://documentation.vkplay.ru/tech_info_vkp/promo_vkp">техническим требованиям</a>
                    </Trans>
                </Alert>
            )}
            <Form onSubmit={onFormSubmit}>
                <Accordion>
                    <Card style={{ cursor: "pointer" }}>
                        <Accordion.Toggle as={Card.Header} eventKey={`event-new`}>
                            <div className={"d-flex align-items-center ml-3"}>
                                <strong className={"ml-3"}>{t("Раздел «новинки»")}</strong>
                            </div>
                        </Accordion.Toggle>
                        <Accordion.Collapse eventKey={`event-new`}>
                            <Card.Body>{t('Арт будет взят из игры из поля "Вертикальная обложка"')}</Card.Body>
                        </Accordion.Collapse>
                    </Card>
                    {blocks.map((block, index) => (
                        <AccordionBlock
                            key={`accordion-${index}`}
                            block={block}
                            index={index}
                            fieldProps={fieldProps}
                            apiData={apiData}
                            setBlocks={setBlocks}
                        />
                    ))}
                    {apiData.forms_data?.main?.fields?.hasOwnProperty.call(
                        apiData.forms_data?.main?.fields || {},
                        "message"
                    ) && <DevForm.Field prefix={"main"} name={"message"} asRow={false} />}
                </Accordion>

                {state === GameFeaturingLaunchState.REVIEW_PASSED && (
                    <Alert variant="success" className="mt-3">
                        {t("Рекламные материалы успешно прошли модерацию.")}
                        <br />
                        <Trans>
                            Для более подробной статистики по размещению используйте{" "}
                            <a href={url_reverse("devs:mytracker", { dev_id: gameCommonInfo.developer.id })}>
                                Mytracker
                            </a>
                        </Trans>
                    </Alert>
                )}

                {state === GameFeaturingLaunchState.PROMO_LAUNCHED && (
                    <Alert variant="success" className="mt-3">
                        {t("Промо запущено.")}
                        <br />
                        <Trans>
                            Для более подробной статистики по размещению используйте{" "}
                            <a href={url_reverse("devs:mytracker", { dev_id: gameCommonInfo.developer.id })}>
                                Mytracker
                            </a>
                        </Trans>
                    </Alert>
                )}

                {state === GameFeaturingLaunchState.REFUSED && (
                    <Alert variant="danger" className="mt-3">
                        {t("Вам отказано в размещении промо.")}
                        <br />
                        {apiData.object.comments?.refused_moderator_comment && (
                            <>
                                <div className="show-wrap-text">
                                    {t("Причина:")} {parseAndLinkify(apiData.object.comments.refused_moderator_comment)}
                                </div>
                            </>
                        )}
                    </Alert>
                )}

                <div className="mt-3 d-flex">
                    {(state === GameFeaturingLaunchState.DRAFT ||
                        state === GameFeaturingLaunchState.REJECTED ||
                        apiData.object == null) && (
                        <>
                            <DevForm.Button className="mr-2" variant="primary" type="submit" name="action" value="save">
                                {t("Сохранить")}
                            </DevForm.Button>

                            <ConditionalWrapper
                                condition={disableSendButton}
                                wrapper={(children) => (
                                    <OverlayTrigger
                                        overlay={
                                            <Tooltip>
                                                {t(
                                                    'Вы сможете отправить материалы по "базовому промо" игры на проверку после выхода игры в релиз'
                                                )}
                                            </Tooltip>
                                        }
                                    >
                                        {children}
                                    </OverlayTrigger>
                                )}
                            >
                                <Button
                                    className="mr-2"
                                    variant="warning"
                                    onClick={() => setShow(true)}
                                    disabled={disableSendButton}
                                >
                                    {t("Отправить на проверку")}
                                </Button>
                            </ConditionalWrapper>

                            <SubmitModal
                                show={show}
                                setShow={setShow}
                                blocks={blocks}
                                setApiData={setApiData}
                                onFormSubmit={onFormSubmit}
                            />
                        </>
                    )}

                    {commonInfo.account.is_super && (
                        <DevForm.Button
                            variant="outline-primary"
                            type="submit"
                            name="action"
                            value="request_basic_promo"
                        >
                            {t("Запросить базовое промо")}
                        </DevForm.Button>
                    )}
                </div>
            </Form>
            &nbsp;
            {apiData?.f_messages && apiData.f_messages.length > 0 && (
                <DeveloperModerMessages f_messages={apiData.f_messages} />
            )}
        </CommonCard>
    );
}
